import { z } from 'zod';
import { dateSchema, idSchema, paginationQuerySchema } from './common.schema.js';
import { MAIL_STATUSES } from './enums.js';
/**
 * A purchase order placed by a site manager.
 *
 * The order leaves as an e-mail to the merchant, which is exactly how it is
 * placed today — the app only replaces the phone call and makes sure the
 * invoice details are on it. It is also stored, because the mail is handed to
 * a worker rather than sent inline: without a row there would be nothing to
 * tell the manager whether their order actually went out. Buyer data is never
 * part of the input; it comes from the deployment's environment.
 */
/** Units a building site actually orders in. */
export const ORDER_UNITS = [
    'szt',
    'kpl',
    'opak',
    'paleta',
    'm²',
    'm³',
    'mb',
    'm',
    'kg',
    't',
    'l',
];
export const orderItemSchema = z.object({
    name: z.string().trim().min(2, 'Podaj nazwę pozycji').max(200),
    quantity: z.number().positive('Ilość musi być większa od zera'),
    unit: z.string().trim().min(1, 'Podaj jednostkę').max(12),
});
/** Where the goods go — never the company address, which is the invoice one. */
export const deliveryAddressSchema = z.object({
    street: z.string().trim().min(3, 'Podaj ulicę i numer').max(120),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{2}-\d{3}$/, 'Kod pocztowy w formacie 00-000'),
    city: z.string().trim().min(2, 'Podaj miasto').max(80),
    contactPhone: z
        .string()
        .trim()
        .min(9, 'Podaj numer kontaktowy')
        .max(20)
        .regex(/^[\d\s+()-]+$/, 'Numer może zawierać tylko cyfry i znaki + ( ) -'),
});
export const createOrderSchema = z.object({
    /**
     * Which site ordered. Required, and the one field with no way around it: an
     * order nobody can tie to a build is the phone call this screen replaces.
     * Spelled out rather than reusing `idSchema`, whose message is written for
     * an id the app forgot to send, not for a picker the manager left empty.
     */
    projectId: z.string().min(1, 'Wybierz budowę'),
    /**
     * Set when the address was picked from the vendor dictionary, `null` when it
     * was typed by hand — a merchant nobody has entered yet still has to be
     * orderable, otherwise the manager reaches for their phone instead.
     */
    vendorId: idSchema.nullish(),
    recipientEmail: z.string().trim().email('Podaj poprawny adres e-mail'),
    items: z.array(orderItemSchema).min(1, 'Dodaj co najmniej jedną pozycję').max(50),
    delivery: deliveryAddressSchema,
    notes: z.string().max(1000).nullish(),
});
/**
 * A placed order, as the manager's history shows it.
 *
 * `status` is the delivery state of the message the order was sent as — the
 * request only queues it, so a freshly placed order is `PENDING` until the
 * worker gets it out the door.
 */
export const orderSchema = z.object({
    id: idSchema,
    status: z.enum(MAIL_STATUSES),
    recipientEmail: z.string(),
    vendorId: idSchema.nullable(),
    /** Copied when the order was placed, so renaming a vendor cannot rewrite history. */
    vendorName: z.string().nullable(),
    /** Both `null` only on orders placed before the site was asked for. */
    projectId: idSchema.nullable(),
    projectName: z.string().nullable(),
    placedById: idSchema,
    placedByName: z.string(),
    placedAt: dateSchema,
    sentAt: dateSchema.nullable(),
    /** How many delivery attempts the worker has made, and why the last one failed. */
    attempts: z.number().int(),
    lastError: z.string().nullable(),
    items: z.array(orderItemSchema),
    delivery: deliveryAddressSchema,
    notes: z.string().nullable(),
});
/**
 * Sortable columns of the history.
 *
 * `recipient` is the merchant's name where one was picked and the address
 * otherwise, `email` the address always — the list shows both, and either can
 * be the one you are looking down.
 */
export const orderSortFields = [
    'placedAt',
    'recipient',
    'email',
    'project',
    'city',
    'status',
];
export const orderListQuerySchema = paginationQuerySchema.extend({
    /** Free-text search over the merchant, its e-mail and the delivery city. */
    search: z.string().trim().optional(),
    status: z.enum(MAIL_STATUSES).optional(),
    projectId: idSchema.optional(),
    /** Both bounds are whole days, inclusive — the API widens `placedTo` to its end. */
    placedFrom: z.coerce.date().optional(),
    placedTo: z.coerce.date().optional(),
    sortBy: z.enum(orderSortFields).default('placedAt'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
});
//# sourceMappingURL=order.schema.js.map